import { ROLE_GROUPS, hasRole, hasModule } from './navigation';
import { HOW_TO_STEPS } from './onboardingMedia';

/**
 * Get walkthrough data for a help topic
 * @param {string} walkthroughId - The walkthrough ID from the topic
 * @returns {Object|null} HOW_TO_STEPS data or null
 */
export function getWalkthroughForTopic(walkthroughId) {
  if (!walkthroughId) return null;
  return HOW_TO_STEPS[walkthroughId] || null;
}

/**
 * Comprehensive help content for the Help Center
 * Each topic includes:
 * - key: Unique identifier (used in URLs)
 * - label: Display title
 * - description: Short summary
 * - category: Grouping category
 * - steps: Step-by-step instructions
 * - tips: Pro tips and best practices
 * - keywords: Search terms
 * - relatedTopics: Links to related help articles
 * - videoUrl: Optional video tutorial URL
 * - cta: Call-to-action button
 * - roles: Role restrictions (empty = all users)
 * - modules: Module restrictions
 */
export const HELP_TOPICS = [
  // ============================================
  // GETTING STARTED
  // ============================================
  {
    key: 'getting-started',
    label: 'Getting Started with Uni-Fund',
    description: 'Learn the basics of navigating and using Uni-Fund effectively.',
    category: 'Getting Started',
    steps: [
      'Log in with your university credentials via Single Sign-On (SSO).',
      'Complete the onboarding wizard to personalize your experience.',
      'Explore the Dashboard to see your tasks, alerts, and key metrics.',
      'Use the global search (Cmd+K or Ctrl+K) to quickly find any page or action.',
      'Check your Account Settings to customize your preferences.',
    ],
    tips: [
      'The Dashboard shows items relevant to your role—check it daily for pending tasks.',
      'Save frequently-used filter combinations for quick access.',
      'Use keyboard shortcuts to navigate faster (press ? to see all shortcuts).',
    ],
    keywords: ['getting started', 'start', 'begin', 'new user', 'first time', 'onboarding', 'tutorial', 'guide'],
    relatedTopics: ['cmdk-search', 'dashboard', 'account-settings'],
    cta: { label: 'Open Dashboard', path: '/dashboard' },
    walkthroughId: 'dashboard',
  },
  {
    key: 'cmdk-search',
    label: 'Use the Command Palette (Cmd+K)',
    description: 'Find pages, actions, and help topics from anywhere in the app.',
    category: 'Getting Started',
    steps: [
      'Press Cmd+K (Mac) or Ctrl+K (Windows/Linux) to open the command palette.',
      'Start typing to search for pages, actions, or help topics.',
      'Use the up/down arrow keys to navigate through results.',
      'Press Enter to open the selected item.',
      'Press Escape to close the palette without selecting.',
    ],
    tips: [
      'Type "transfer" to quickly find budget transfer options.',
      'Search for "settings" to jump to any settings page.',
      'Recent searches appear at the top for quick repeat access.',
      'You can search for help topics directly from the command palette.',
    ],
    keywords: ['command palette', 'cmd+k', 'ctrl+k', 'global search', 'palette', 'search', 'keyboard', 'shortcut'],
    relatedTopics: ['getting-started', 'keyboard-shortcuts'],
    videoUrl: '/assets/videos/search-tutorial.mp4',
    walkthroughId: 'search',
  },
  {
    key: 'dashboard',
    label: 'Understanding Your Dashboard',
    description: 'See pending approvals, notifications, and key metrics at a glance.',
    category: 'Getting Started',
    steps: [
      'Open the Dashboard from the main navigation or by pressing Cmd+K and typing "dashboard".',
      'Review the "Pending Items" section for tasks requiring your attention.',
      'Check the alerts panel for important notifications and deadlines.',
      'View key metrics and KPIs relevant to your departments.',
      'Click any card to jump directly to the related page or item.',
    ],
    tips: [
      'Configure your dashboard widgets in Account Settings to show what matters most.',
      'Dashboard metrics refresh automatically every few minutes.',
      'Click on metric values to drill down into detailed reports.',
      'Use the date range selector to compare different periods.',
    ],
    keywords: ['dashboard', 'home', 'overview', 'tasks', 'pending', 'notifications', 'alerts', 'metrics'],
    relatedTopics: ['getting-started', 'quick-actions'],
    cta: { label: 'Open Dashboard', path: '/dashboard' },
    walkthroughId: 'dashboard',
  },
  {
    key: 'quick-actions',
    label: 'Using Quick Actions',
    description: 'Access common tasks with a single click from anywhere in the app.',
    category: 'Getting Started',
    steps: [
      'Look for the blue Quick Actions button (lightning bolt icon) in the bottom-right corner.',
      'Click the button to see available quick actions based on your role.',
      'Select an action to open the relevant form or page.',
      'Common actions include creating transfers, submitting requests, and running reports.',
    ],
    tips: [
      'Quick Actions adapt to your current context—different pages show different options.',
      'The most frequently used actions appear at the top.',
      'You can also access Quick Actions through the command palette.',
    ],
    keywords: ['quick actions', 'shortcuts', 'fast', 'common tasks', 'fab', 'button', 'actions'],
    relatedTopics: ['cmdk-search', 'keyboard-shortcuts'],
    walkthroughId: 'quick-actions',
  },
  {
    key: 'keyboard-shortcuts',
    label: 'Keyboard Shortcuts Reference',
    description: 'Master keyboard shortcuts to work faster in Uni-Fund.',
    category: 'Getting Started',
    steps: [
      'Press ? (question mark) anywhere in the app to see available shortcuts.',
      'Cmd+K / Ctrl+K: Open global search',
      'Cmd+/ / Ctrl+/: Toggle keyboard shortcut help',
      'Escape: Close modals and dialogs',
      'Arrow keys: Navigate through lists and tables',
    ],
    tips: [
      'Most shortcuts work the same on Mac and Windows, just swap Cmd for Ctrl.',
      'Shortcuts are disabled when typing in text fields.',
      'Learn the top 5 shortcuts first, then add more as needed.',
    ],
    keywords: ['keyboard', 'shortcuts', 'hotkeys', 'keys', 'speed', 'efficiency', 'navigation'],
    relatedTopics: ['cmdk-search', 'quick-actions'],
    walkthroughId: 'keyboard-shortcuts',
  },

  // ============================================
  // BUDGET MANAGEMENT
  // ============================================
  {
    key: 'budget-overview',
    label: 'View a Department Budget Overview',
    description: 'Explore budgets by department, scenario, and fiscal year.',
    category: 'Budget Management',
    steps: [
      'Navigate to Budget Overview from the main menu.',
      'Use the fiscal year dropdown to select the period you want to view.',
      'Choose a scenario (e.g., Approved, Working, Forecast) to see different projections.',
      'Filter by department, fund, or account type as needed.',
      'Click on any department row to drill into detailed budget lines.',
    ],
    tips: [
      'Compare scenarios side-by-side to understand budget variations.',
      'Export data to Excel for offline analysis using the export button.',
      'Bookmark specific filter combinations for quick access later.',
      'Use the variance column to identify significant budget differences.',
    ],
    keywords: ['budget', 'overview', 'department', 'fiscal year', 'scenario', 'summary', 'view', 'comparison'],
    relatedTopics: ['planning', 'budget-scenarios', 'transfers-create'],
    cta: { label: 'Open Budget Overview', path: '/budget' },
  },
  {
    key: 'planning',
    label: 'Update a Department Plan',
    description: 'Edit plan lines and assumptions for a fiscal year.',
    category: 'Budget Management',
    steps: [
      'Open Planning from the main navigation menu.',
      'Select the department and fiscal year you want to update.',
      'Review current plan lines and their assumptions.',
      'Click on a line item to edit values, notes, or assumptions.',
      'Use the bulk edit feature for multiple line changes.',
      'Save your changes—they will enter the approval workflow if required.',
    ],
    tips: [
      'Use comments to document the reasoning behind changes.',
      'Check the audit trail to see historical changes.',
      'Plan changes may trigger approval workflows based on thresholds.',
      'Use "Copy from Previous Year" to start with last year\'s values.',
    ],
    keywords: ['planning', 'plan', 'forecast', 'scenario', 'edit', 'update', 'budget plan', 'line items'],
    relatedTopics: ['budget-overview', 'budget-scenarios', 'transfers-create'],
    cta: { label: 'Open Planning', path: '/planning' },
  },
  {
    key: 'budget-scenarios',
    label: 'Working with Budget Scenarios',
    description: 'Create and compare different budget scenarios for planning.',
    category: 'Budget Management',
    steps: [
      'Navigate to Budget > Scenarios from the main menu.',
      'View existing scenarios or create a new one.',
      'To create a scenario, click "New Scenario" and choose a base to copy from.',
      'Name your scenario descriptively (e.g., "10% Enrollment Increase").',
      'Make changes to the scenario without affecting the approved budget.',
      'Use the comparison tool to see differences between scenarios.',
    ],
    tips: [
      'Scenarios are isolated—changes don\'t affect other scenarios until merged.',
      'Use scenarios for "what-if" analysis before committing to changes.',
      'Export scenario comparisons for leadership presentations.',
      'You can lock scenarios to prevent further editing.',
    ],
    keywords: ['scenarios', 'what-if', 'planning', 'comparison', 'modeling', 'forecast', 'projections'],
    roles: ROLE_GROUPS.BUDGET,
    relatedTopics: ['budget-overview', 'planning'],
    cta: { label: 'Open Scenarios', path: '/budget/scenarios' },
    walkthroughId: 'budget-scenarios',
  },
  {
    key: 'transfers-create',
    label: 'Create a Budget Transfer',
    description: 'Move funding between departments, positions, or accounts.',
    category: 'Budget Management',
    steps: [
      'Navigate to Transfers from the main menu.',
      'Click "New Transfer" to start the transfer wizard.',
      'Select the source (where funds are coming from).',
      'Select the destination (where funds are going).',
      'Enter the transfer amount and effective date.',
      'Add notes explaining the reason for the transfer.',
      'Review the summary and submit for approval.',
    ],
    tips: [
      'Transfers require approval based on your organization\'s thresholds.',
      'You can save transfers as drafts to complete later.',
      'Check fund restrictions before creating cross-fund transfers.',
      'Use batch transfers for multiple related movements.',
    ],
    keywords: ['transfer', 'budget transfer', 'move funds', 'reallocate', 'submit', 'create', 'request'],
    relatedTopics: ['transfers-approve', 'budget-overview'],
    cta: { label: 'Open Transfers', path: '/transfers' },
    walkthroughId: 'budget-transfers',
  },
  {
    key: 'transfers-approve',
    label: 'Approve or Reject Transfers',
    description: 'Review transfer requests and make approval decisions.',
    category: 'Budget Management',
    steps: [
      'Open Transfers and filter to show "Pending" status.',
      'Click on a transfer to view its details.',
      'Review the source, destination, amount, and justification.',
      'Check the audit trail for any prior approvals in the chain.',
      'Click "Approve" or "Reject" with an optional comment.',
      'For rejections, provide feedback so the requester can resubmit.',
    ],
    tips: [
      'You\'ll receive notifications when transfers need your approval.',
      'Use the bulk approve feature for routine transfers.',
      'Check budget availability before approving large transfers.',
      'Approval history is fully auditable for compliance.',
    ],
    keywords: ['approve', 'reject', 'pending', 'transfer approval', 'review', 'authorization'],
    roles: ROLE_GROUPS.FINANCE,
    relatedTopics: ['transfers-create', 'dashboard'],
    cta: { label: 'Open Transfers', path: '/transfers' },
    walkthroughId: 'transfers-approve',
  },

  // ============================================
  // RESEARCH MANAGEMENT
  // ============================================
  {
    key: 'research-overview',
    label: 'Research Portfolio Overview',
    description: 'Track all your grants, sponsored projects, and research funding.',
    category: 'Research Management',
    steps: [
      'Navigate to Research from the main menu.',
      'View your active grants and their current status.',
      'Filter by funding agency, project type, or PI.',
      'Click on any project to see detailed financials.',
      'Track spending against budgeted amounts in real-time.',
    ],
    tips: [
      'Set up alerts for grants approaching their end date.',
      'Use the timeline view to see project milestones.',
      'Export project summaries for sponsor reporting.',
      'Link related projects to see combined financials.',
    ],
    keywords: ['research', 'grants', 'sponsored', 'projects', 'portfolio', 'funding', 'PI'],
    modules: ['research'],
    relatedTopics: ['research-commitments', 'research-noa'],
    cta: { label: 'Open Research', path: '/research' },
    walkthroughId: 'research-portfolio',
  },
  {
    key: 'research-commitments',
    label: 'Manage Research Commitments',
    description: 'Track cost-sharing and commitment obligations.',
    category: 'Research Management',
    steps: [
      'Navigate to Research and select the Commitments tab.',
      'View all active commitments and their fulfillment status.',
      'Click "New Commitment" to create a new entry.',
      'Enter commitment details: type, amount, period, and funding source.',
      'Link to the related grant or project.',
      'Track commitment fulfillment through periodic entries.',
    ],
    tips: [
      'Set up reminders for commitment reporting deadlines.',
      'Document commitment modifications with detailed notes.',
      'Use the projection feature to forecast commitment obligations.',
      'Commitments affect available budget calculations.',
    ],
    keywords: ['research', 'commitment', 'cost-sharing', 'matching', 'obligations', 'grant'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-noa'],
    cta: { label: 'Open Research', path: '/research?tab=commitments' },
    walkthroughId: 'research-commitments',
  },
  {
    key: 'research-noa',
    label: 'Upload and Process NOAs',
    description: 'Upload Notice of Award documents for automatic data extraction.',
    category: 'Research Management',
    steps: [
      'Navigate to Research and click "Upload NOA".',
      'Drag and drop or browse to select your NOA document (PDF).',
      'The system will automatically extract key information.',
      'Review the extracted data for accuracy.',
      'Make corrections if needed and confirm the details.',
      'The grant will be created with the extracted information.',
    ],
    tips: [
      'Supported formats include PDF and scanned documents.',
      'Complex NOAs may require manual review of extracted data.',
      'Save original documents in the project record for reference.',
      'Bulk upload is available for multiple NOAs.',
    ],
    keywords: ['noa', 'notice of award', 'upload', 'document', 'extraction', 'ocr', 'grant'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-commitments'],
    cta: { label: 'Upload NOA', path: '/research?action=upload' },
    walkthroughId: 'research-noa',
  },
  {
    key: 'research-auto-approval',
    label: 'Research Assignment Auto-Approval Roles',
    description: 'Learn how staff assignment approvals work and how to configure which roles can auto-approve.',
    category: 'Research Management',
    steps: [
      'Navigate to Research > Settings from the sidebar.',
      'Scroll to the "Auto-Approval Roles" section.',
      'Review the available roles that can be granted auto-approval privileges.',
      'Click on role cards to toggle them on (blue) or off (gray).',
      'Click "Save defaults" to apply your changes.',
    ],
    tips: [
      'Users with auto-approval roles can assign staff to awards immediately without requiring approval.',
      'Users without auto-approval roles will have their assignments queued for approval by someone who does.',
      'System administrators always have auto-approval rights regardless of this configuration.',
      'Default auto-approval roles are: Finance Director, Research Director, and Department Head.',
      'Consider your institution size when configuring: small teams may want more roles, large offices may want fewer.',
    ],
    keywords: ['research', 'approval', 'auto-approval', 'roles', 'assignment', 'workflow', 'permissions', 'staff'],
    modules: ['research'],
    roles: ROLE_GROUPS.FINANCE,
    relatedTopics: ['research-overview', 'research-explorer', 'research-workload'],
    cta: { label: 'Open Research Settings', path: '/research/settings' },
  },
  {
    key: 'research-explorer',
    label: 'Research Portfolio Explorer',
    description: 'Visualize and explore your research portfolio with interactive views.',
    category: 'Research Management',
    steps: [
      'Navigate to Research > Explorer from the sidebar.',
      'View your portfolio as a hierarchical tree, network graph, or timeline.',
      'Use filters to focus on specific sponsors, statuses, or date ranges.',
      'Click on any node to see detailed information or drill down.',
      'Enable Assignment Mode to drag-and-drop staff onto awards.',
    ],
    tips: [
      'Use the tree view for understanding organizational hierarchy.',
      'The network graph shows relationships between awards and personnel.',
      'Toggle "Show Team Members" to see who is assigned to each award.',
      'In Assignment Mode, staff workload scores help balance assignments.',
    ],
    keywords: ['research', 'explorer', 'portfolio', 'visualization', 'tree', 'graph', 'timeline', 'assign'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-auto-approval', 'research-workload'],
    cta: { label: 'Open Research Explorer', path: '/research/explorer' },
    walkthroughId: 'research-explorer',
  },
  {
    key: 'research-workload',
    label: 'Staff Workload & Complexity Scores',
    description: 'Understand how workload is calculated and displayed for research staff.',
    category: 'Research Management',
    steps: [
      'Open the Research Explorer and enable Assignment Mode.',
      'View workload scores next to each staff member in the panel.',
      'Hover over a staff member to see their total complexity score and award count.',
      'Use the color-coded indicators: green (low), yellow (medium), red (high workload).',
      'Consider workload balance when making new assignments.',
    ],
    tips: [
      'Complexity scores factor in: award amount, personnel count, subawards, sponsor type, and cost sharing.',
      'Federal awards typically score higher due to increased compliance requirements.',
      'Awards with multiple subawards add complexity due to subrecipient monitoring.',
      'Administrators can adjust the complexity formula weights in Research Settings.',
      'A balanced workload across team members helps ensure quality grant management.',
    ],
    keywords: ['research', 'workload', 'complexity', 'score', 'staff', 'bandwidth', 'assignment', 'balance'],
    modules: ['research'],
    relatedTopics: ['research-explorer', 'research-auto-approval'],
    cta: { label: 'Open Research Explorer', path: '/research/explorer' },
    walkthroughId: 'research-workload',
  },
  {
    key: 'research-analytics',
    label: 'Research Analytics Dashboard',
    description: 'Analyze proposal win rates, funding trends, and portfolio performance metrics.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Analytics from the sidebar.',
      'View summary cards showing total proposals, win rate, and funding amounts.',
      'Use the group-by selector to analyze by department, PI, or sponsor.',
      'Review the win rate chart to identify top-performing areas.',
      'Analyze funding trends over time using the trend chart.',
      'Export analytics data for reporting.',
    ],
    tips: [
      'Compare win rates across sponsors to identify strategic funding opportunities.',
      'Use department grouping to benchmark performance across units.',
      'Track trends over multiple fiscal years to identify patterns.',
      'Share analytics with leadership using the export feature.',
    ],
    keywords: ['research', 'analytics', 'win rate', 'funding', 'trends', 'proposals', 'performance', 'metrics'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-portfolio-performance', 'research-financial-health'],
    cta: { label: 'Open Research Analytics', path: '/research/analytics' },
    walkthroughId: 'research-analytics',
  },
  {
    key: 'research-financial-health',
    label: 'Research Financial Health',
    description: 'Monitor burn rates, spending patterns, and financial status of your research portfolio.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Financial Health.',
      'Review the burn rate summary showing overall spending status.',
      'Check the burn rate distribution chart for portfolio-wide view.',
      'Identify underspending or overspending awards using color indicators.',
      'Click on individual awards for detailed spending patterns.',
      'Configure alerts for awards approaching budget thresholds.',
    ],
    tips: [
      'Green indicates on-track spending relative to project timeline.',
      'Yellow flags underspending which may indicate delayed project activities.',
      'Red indicates overspending requiring immediate attention.',
      'Review financial health monthly to catch issues early.',
    ],
    keywords: ['research', 'financial', 'health', 'burn rate', 'spending', 'budget', 'overspending', 'underspending'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-analytics', 'research-closeout'],
    cta: { label: 'Open Financial Health', path: '/research/financial-health' },
    walkthroughId: 'research-financial-health',
  },
  {
    key: 'research-portfolio-performance',
    label: 'Research Portfolio Performance',
    description: 'Analyze award performance by sponsor, PI, department, and other dimensions.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Portfolio Performance.',
      'View summary metrics: total awards, funding amounts, and activity counts.',
      'Use the group-by selector to analyze by sponsor, PI, or department.',
      'Review the performance chart showing top performers.',
      'Analyze award duration and average amounts by category.',
      'Export performance data for stakeholder reporting.',
    ],
    tips: [
      'Track performance trends over time to identify growing areas.',
      'Compare sponsor mix to understand funding diversification.',
      'Use PI analysis for faculty development and mentoring discussions.',
      'Share performance dashboards with department chairs.',
    ],
    keywords: ['research', 'portfolio', 'performance', 'awards', 'funding', 'analysis', 'metrics', 'reporting'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-analytics', 'research-explorer'],
    cta: { label: 'Open Portfolio Performance', path: '/research/portfolio-performance' },
    walkthroughId: 'research-portfolio-performance',
  },
  {
    key: 'research-personnel-effort',
    label: 'Research Personnel Effort',
    description: 'Track personnel effort allocations and identify over-commitment issues.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Personnel Effort.',
      'View the effort summary showing total personnel and allocations.',
      'Review effort distribution by role (PI, Co-PI, Staff, etc.).',
      'Check for over-committed individuals exceeding 100% effort.',
      'View the detailed effort table for all personnel assignments.',
      'Filter by department or sponsor to focus your analysis.',
    ],
    tips: [
      'Effort certification deadlines require accurate effort records.',
      'Flag personnel approaching 100% commitment before new proposals.',
      'Review effort at proposal submission to prevent over-commitment.',
      'Coordinate with HR for cost-sharing effort documentation.',
    ],
    keywords: ['research', 'personnel', 'effort', 'certification', 'allocation', 'PI', 'Co-PI', 'commitment'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-compliance', 'research-workload'],
    cta: { label: 'Open Personnel Effort', path: '/research/personnel-effort' },
    walkthroughId: 'research-personnel-effort',
  },
  {
    key: 'research-compliance',
    label: 'Research Compliance & Risk',
    description: 'Monitor compliance status and risk indicators across your research portfolio.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Compliance & Risk.',
      'View the compliance dashboard with risk indicators.',
      'Review flagged items requiring immediate attention.',
      'Check effort certification status for all personnel.',
      'Monitor cost sharing commitments and fulfillment.',
      'Generate compliance reports for audits.',
    ],
    tips: [
      'Address high-risk items within 5 business days.',
      'Schedule quarterly compliance reviews with your team.',
      'Document all compliance actions for audit trail.',
      'Set up automatic reminders for certification deadlines.',
    ],
    keywords: ['research', 'compliance', 'risk', 'audit', 'certification', 'cost sharing', 'monitoring'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-personnel-effort', 'research-closeout'],
    cta: { label: 'Open Compliance', path: '/research/compliance' },
    walkthroughId: 'research-compliance',
  },
  {
    key: 'research-closeout',
    label: 'Research Closeout Readiness',
    description: 'Prepare for grant closeout with checklists and deadline tracking.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Closeout Readiness.',
      'View grants ending within the next 90 days.',
      'Check the closeout checklist for each grant.',
      'Review outstanding obligations and encumbrances.',
      'Verify all required reports are submitted.',
      'Complete final financial reconciliation.',
    ],
    tips: [
      'Start closeout preparation 90 days before grant end.',
      'Use the timeline view to see all upcoming closeouts.',
      'Set up automatic reminders for PI and department admin.',
      'Document any final cost transfers with justification.',
    ],
    keywords: ['research', 'closeout', 'ending', 'checklist', 'reconciliation', 'reports', 'deadline'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-financial-health', 'research-compliance'],
    cta: { label: 'Open Closeout', path: '/research/closeout' },
    walkthroughId: 'research-closeout',
  },
  {
    key: 'research-proposals',
    label: 'Research Proposals Management',
    description: 'Track proposals through their lifecycle from concept to award.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Proposals.',
      'View all proposals organized by stage.',
      'Click "New Proposal" to create a new entry.',
      'Enter proposal details: title, sponsor, amounts, and dates.',
      'Update stages as proposals progress through review.',
      'Link successful proposals to resulting awards.',
    ],
    tips: [
      'Use the pipeline view to see proposals by stage.',
      'Set internal deadlines before sponsor deadlines.',
      'Track win/loss rates to improve future submissions.',
      'Document decline reasons for strategic planning.',
    ],
    keywords: ['research', 'proposals', 'pipeline', 'submission', 'stage', 'funding', 'sponsor'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-analytics', 'research-budget-builder'],
    cta: { label: 'Open Proposals', path: '/research/proposals' },
    walkthroughId: 'research-proposals',
  },
  {
    key: 'research-budget-builder',
    label: 'Research Budget Builder',
    description: 'Build detailed proposal budgets with automated calculations.',
    category: 'Research Management',
    steps: [
      'Open a proposal and click "Build Budget".',
      'Add personnel with effort percentages and salary rates.',
      'Include equipment, travel, and supplies by category.',
      'Add subaward budgets for subrecipients.',
      'Review automatic F&A calculations.',
      'Export budget to sponsor-required format.',
    ],
    tips: [
      'Use budget templates for common proposal types.',
      'F&A rates are applied automatically based on settings.',
      'Multi-year budgets can include escalation rates.',
      'Save drafts frequently during budget development.',
    ],
    keywords: ['research', 'budget', 'builder', 'proposal', 'F&A', 'indirect', 'personnel', 'costs'],
    modules: ['research'],
    relatedTopics: ['research-proposals', 'research-overview', 'research-settings'],
    cta: { label: 'Open Proposals', path: '/research/proposals' },
    walkthroughId: 'research-budget-builder',
  },
  {
    key: 'research-subawards',
    label: 'Subaward Management',
    description: 'Track and manage subawards and subrecipient monitoring.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Subawards.',
      'View all active subawards and their status.',
      'Click on a subaward to see details and history.',
      'Monitor invoicing and payment status.',
      'Review risk assessments and compliance requirements.',
      'Track closeout progress for ending subawards.',
    ],
    tips: [
      'Set up invoice reminders to ensure timely payments.',
      'Conduct periodic risk assessments of subrecipients.',
      'Document all modifications for audit purposes.',
      'Start subaward closeout 60 days before end date.',
    ],
    keywords: ['research', 'subaward', 'subrecipient', 'monitoring', 'invoice', 'compliance', 'risk'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-compliance', 'research-closeout'],
    cta: { label: 'Open Subawards', path: '/research/subawards' },
    walkthroughId: 'research-subawards',
  },
  {
    key: 'research-detail',
    label: 'Individual Award Details',
    description: 'View and manage detailed information for a specific research award.',
    category: 'Research Management',
    steps: [
      'Click on an award from Research Portfolio or Explorer.',
      'View award overview: sponsor, dates, status, and amounts.',
      'Review budget breakdown by category and period.',
      'Check personnel assignments and effort allocations.',
      'Configure link rules to connect transactions automatically.',
      'Use AI insights for spending recommendations.',
    ],
    tips: [
      'Set up link rules early to automate transaction categorization.',
      'Monitor burn rate against project timeline.',
      'Review personnel effort quarterly for certification accuracy.',
      'Document any budget modifications in the notes section.',
    ],
    keywords: ['research', 'award', 'detail', 'grant', 'budget', 'personnel', 'transactions', 'link rules'],
    modules: ['research'],
    relatedTopics: ['research-overview', 'research-explorer', 'research-financial-health'],
    cta: { label: 'Open Research', path: '/research' },
    walkthroughId: 'research-detail',
  },
  {
    key: 'research-settings',
    label: 'Research Module Settings',
    description: 'Configure F&A rates, approval workflows, and other research settings.',
    category: 'Research Management',
    steps: [
      'Navigate to Research → Settings (requires admin access).',
      'Configure F&A rate and base for indirect cost calculations.',
      'Set up auto-approval roles for workflow automation.',
      'Adjust complexity weights for workload scoring.',
      'Configure alert thresholds for budget warnings.',
      'Manage Gantt chart templates for proposal planning.',
    ],
    tips: [
      'Update F&A rates annually or when negotiated rates change.',
      'Test workflow changes in a sandbox before production.',
      'Document complexity weight rationale for consistency.',
      'Review alert thresholds with your finance team.',
    ],
    keywords: ['research', 'settings', 'F&A', 'indirect', 'approval', 'workflow', 'configuration', 'admin'],
    modules: ['research'],
    roles: ROLE_GROUPS.FINANCE,
    relatedTopics: ['research-overview', 'research-auto-approval', 'research-workload'],
    cta: { label: 'Open Settings', path: '/research/settings' },
    walkthroughId: 'research-settings',
  },

  // ============================================
  // POSITION MANAGEMENT
  // ============================================
  {
    key: 'positions',
    label: 'Position Budgeting',
    description: 'Create and manage position budgets with forecasting tools.',
    category: 'Position Management',
    steps: [
      'Navigate to Positions > Budgeting from the main menu.',
      'View all positions in your department(s).',
      'Click "Add Position" to create a new position budget.',
      'Enter position details: title, FTE, salary, benefits, and funding.',
      'Assign the position to a cost center and fund.',
      'Save and submit for approval if required.',
    ],
    tips: [
      'Use position templates for common position types.',
      'Benefits are calculated automatically based on your institution\'s rates.',
      'Split-funded positions can have multiple funding sources.',
      'View position history to see past changes and incumbents.',
    ],
    keywords: ['positions', 'pcn', 'fte', 'staffing', 'salary', 'benefits', 'budgeting', 'personnel'],
    modules: ['positions'],
    relatedTopics: ['personnel-whatif', 'planning'],
    cta: { label: 'Open Position Budgeting', path: '/positions/budgeting' },
    walkthroughId: 'positions',
  },
  {
    key: 'personnel-whatif',
    label: 'Personnel What-If Scenarios',
    description: 'Model staffing changes and see financial impacts instantly.',
    category: 'Position Management',
    steps: [
      'Navigate to Position Budgeting.',
      'Click the "Personnel what-if" button to expand the analysis panel.',
      'Set a default percentage change or override per person.',
      'View the real-time financial impact including department rollups.',
      'Compare current vs what-if totals across departments.',
    ],
    tips: [
      'What-if scenarios don\'t affect actual budgets until approved.',
      'Include benefits and indirect costs in your analysis.',
      'Use department filters to focus on specific areas.',
      'The department rollup shows aggregate impact by department.',
    ],
    keywords: ['what-if', 'personnel', 'staffing', 'modeling', 'scenarios', 'impact', 'analysis'],
    modules: ['positions'],
    roles: ROLE_GROUPS.HR,
    relatedTopics: ['positions', 'budget-scenarios'],
    cta: { label: 'Open Position Budgeting', path: '/positions/budgeting' },
    walkthroughId: 'personnel-whatif',
  },

  // ============================================
  // FINANCE & ACCOUNTING
  // ============================================
  {
    key: 'revenue-recognition',
    label: 'Revenue Recognition',
    description: 'Manage deferred revenue and recognition schedules.',
    category: 'Finance & Accounting',
    steps: [
      'Navigate to Budget > Revenue Recognition.',
      'View current deferred revenue balances by source.',
      'Create recognition schedules for new revenue streams.',
      'Set recognition rules: straight-line, milestone-based, or custom.',
      'Run the recognition process to post entries.',
      'Review and approve journal entries before posting.',
    ],
    tips: [
      'Set up recurring schedules for predictable revenue streams.',
      'Use the forecast view to project future recognition.',
      'Reconcile deferred balances monthly for accuracy.',
      'Document any manual adjustments with detailed notes.',
    ],
    keywords: ['revenue', 'recognition', 'deferred', 'accounting', 'journal', 'posting', 'schedule'],
    modules: ['advanced_finance'],
    roles: ROLE_GROUPS.FINANCE,
    relatedTopics: ['program-margins', 'budget-overview'],
    cta: { label: 'Open Revenue Recognition', path: '/budget/revenue-recognition' },
    walkthroughId: 'revenue-recognition',
  },
  {
    key: 'program-margins',
    label: 'Program Margin Analysis',
    description: 'Analyze program profitability and identify opportunities.',
    category: 'Finance & Accounting',
    steps: [
      'Navigate to Budget > Program Margins.',
      'Select the programs or departments to analyze.',
      'View revenue, direct costs, and margin calculations.',
      'Drill down into cost drivers and revenue sources.',
      'Compare margins across programs and time periods.',
      'Export reports for stakeholder presentations.',
    ],
    tips: [
      'Use consistent allocation methods for accurate comparisons.',
      'Benchmark against prior years to identify trends.',
      'Consider indirect costs when evaluating true program profitability.',
      'Set margin targets and track progress over time.',
    ],
    keywords: ['margins', 'profitability', 'analysis', 'programs', 'revenue', 'costs', 'performance'],
    modules: ['advanced_finance'],
    roles: ROLE_GROUPS.FINANCE,
    relatedTopics: ['revenue-recognition', 'budget-vs-actual'],
    cta: { label: 'Open Margin Analysis', path: '/budget/margins' },
    walkthroughId: 'program-margins',
  },
  {
    key: 'budget-vs-actual',
    label: 'Budget vs Actual Reports',
    description: 'Compare budgeted amounts to actual spending and revenue.',
    category: 'Finance & Accounting',
    steps: [
      'Navigate to Budget > vs Actual from the main menu.',
      'Select the fiscal year and period range.',
      'Choose departments, funds, or accounts to include.',
      'View the variance between budget and actual.',
      'Drill into specific line items for transaction details.',
      'Export the report in Excel or PDF format.',
    ],
    tips: [
      'Set up variance thresholds to highlight significant differences.',
      'Use the trend view to see patterns over multiple periods.',
      'Schedule automated reports to run monthly.',
      'Add comments to explain known variances.',
    ],
    keywords: ['budget', 'actual', 'variance', 'comparison', 'report', 'spending', 'analysis'],
    relatedTopics: ['budget-overview', 'reports'],
    cta: { label: 'Open Budget vs Actual', path: '/budget/vs-actual' },
    walkthroughId: 'budget-vs-actual',
  },

  // ============================================
  // COURSES & ENROLLMENT
  // ============================================
  {
    key: 'courses',
    label: 'Course Section Planning',
    description: 'Create, import, and manage course sections.',
    category: 'Courses & Enrollment',
    steps: [
      'Navigate to Courses from the main menu.',
      'Select the term you want to plan.',
      'View existing sections or import from the course catalog.',
      'Create new sections by clicking "Add Section".',
      'Set capacity, instructor, and schedule details.',
      'Submit sections for departmental approval.',
    ],
    tips: [
      'Use historical enrollment data to set realistic capacities.',
      'Check room availability before finalizing schedules.',
      'Link sections to instructor workload calculations.',
      'Track enrollment against planned capacity in real-time.',
    ],
    keywords: ['courses', 'sections', 'planning', 'enrollment', 'capacity', 'scheduling', 'terms'],
    modules: ['courses'],
    relatedTopics: ['enrollment-yield', 'enrollment-funnel'],
    cta: { label: 'Open Course Planning', path: '/courses' },
    walkthroughId: 'courses',
  },
  {
    key: 'enrollment-funnel',
    label: 'Admission Funnel Analysis',
    description: 'Track applicants through the admission process.',
    category: 'Courses & Enrollment',
    steps: [
      'Navigate to Enrollment > Funnel from the main menu.',
      'View the funnel stages: Applied, Admitted, Deposited, Enrolled.',
      'Compare current cycle to prior years.',
      'Analyze conversion rates between stages.',
      'Filter by program, term, or demographic attributes.',
      'Set targets and track progress toward enrollment goals.',
    ],
    tips: [
      'Monitor funnel daily during peak admission periods.',
      'Set up alerts for significant changes in conversion rates.',
      'Use the cohort comparison to identify trends.',
      'Export data for enrollment management meetings.',
    ],
    keywords: ['enrollment', 'funnel', 'admission', 'applicants', 'conversion', 'yield', 'tracking'],
    modules: ['enrollment'],
    relatedTopics: ['enrollment-yield', 'courses'],
    cta: { label: 'Open Funnel Analysis', path: '/enrollment/funnel' },
  },
  {
    key: 'enrollment-yield',
    label: 'Yield Analysis Dashboard',
    description: 'Analyze and predict enrollment yield rates.',
    category: 'Courses & Enrollment',
    steps: [
      'Navigate to Enrollment > Yield Analysis.',
      'View current yield rates by program and population.',
      'Compare to historical yield trends.',
      'Use predictive models to forecast final enrollment.',
      'Adjust assumptions to model different scenarios.',
      'Track actual yield against predictions as deposits arrive.',
    ],
    tips: [
      'Update predictions weekly during the deposit season.',
      'Consider external factors (economy, competition) in forecasts.',
      'Use yield data to inform financial aid strategy.',
      'Share projections with leadership for planning purposes.',
    ],
    keywords: ['yield', 'enrollment', 'prediction', 'forecast', 'analysis', 'trends', 'deposits'],
    modules: ['enrollment'],
    relatedTopics: ['enrollment-funnel', 'budget-scenarios'],
    cta: { label: 'Open Yield Analysis', path: '/enrollment/yield' },
  },

  // ============================================
  // CAPITAL PLANNING
  // ============================================
  {
    key: 'capital-planning',
    label: 'Capital Planning & Projections',
    description: 'Plan multi-year capital spend, cash flow, and funding impacts.',
    category: 'Capital Planning',
    steps: [
      'Navigate to Capital > Planning.',
      'Choose a scenario and base fiscal year.',
      'Set the projection horizon and apply status or department filters.',
      'Review summary cards and charts for spend, funding, and cash flow.',
      'Apply what-if adjustments such as inflation or timeline shifts.',
      'Export the projection or write plan lines to the budget model.',
    ],
    tips: [
      'Start with the baseline scenario before creating alternatives.',
      'Save what-if adjustments as a named scenario for comparisons.',
      'Use timeline shifts to test delays or accelerated delivery.',
      'Edit cash flow timing in the planning grid for key projects.',
    ],
    keywords: ['capital', 'planning', 'projects', 'projection', 'cash flow', 'debt service', 'scenario'],
    modules: ['capital'],
    relatedTopics: ['budget-scenarios', 'reports'],
    cta: { label: 'Open Capital Planning', path: '/capital/planning' },
    walkthroughId: 'capital-planning',
  },
  {
    key: 'capital-portfolio',
    label: 'Capital Projects Portfolio',
    description: 'Review project status, budgets, and portfolio health.',
    category: 'Capital Planning',
    steps: [
      'Navigate to Capital > Portfolio.',
      'Filter by status, department, or project type.',
      'Review portfolio KPIs and the project list.',
      'Open a project to view details, budgets, and timeline.',
      'Export the portfolio view when needed.',
    ],
    tips: [
      'Use status filters to focus on approved or in-progress projects.',
      'Open projects in new tabs when comparing details.',
      'Check variance and risk indicators before portfolio reviews.',
      'Keep project metadata current for reliable reporting.',
    ],
    keywords: ['capital', 'portfolio', 'projects', 'status', 'budget', 'risk', 'health'],
    modules: ['capital'],
    relatedTopics: ['capital-planning', 'capital-project-detail', 'reports'],
    cta: { label: 'Open Capital Portfolio', path: '/capital/portfolio' },
    walkthroughId: 'capital-portfolio',
  },
  {
    key: 'capital-project-management',
    label: 'Capital Project Management',
    description: 'Manage budgets, timelines, approvals, and risks for a single project.',
    category: 'Capital Planning',
    steps: [
      'Navigate to Capital > Projects or Portfolio.',
      'Open a project from the list.',
      'Review the overview, budgets, and cash flow tabs.',
      'Update timelines, phases, and dependencies as needed.',
      'Track risks, issues, change orders, and invoices.',
      'Submit the project for approval when ready.',
    ],
    tips: [
      'Attach supporting documents for approvals and audits.',
      'Keep project status and priority current for portfolio reporting.',
      'Use variance and health indicators to catch issues early.',
      'Check alerts for compliance and milestone deadlines.',
    ],
    keywords: ['capital', 'project', 'management', 'budget', 'timeline', 'risk', 'issues', 'invoices'],
    modules: ['capital'],
    relatedTopics: ['capital-portfolio', 'capital-planning', 'capital-project-detail'],
    cta: { label: 'Open Capital Projects', path: '/capital' },
    walkthroughId: 'capital-projects',
  },

  // ============================================
  // REPORTS & ANALYTICS
  // ============================================
  {
    key: 'reports',
    label: 'Analytics BI & Dashboards',
    description: 'Access built-in reports and analytics BI dashboards.',
    category: 'Reports & Analytics',
    steps: [
      'Navigate to Analytics BI from the main menu.',
      'Browse available reports by category.',
      'Click on a report to open it with default parameters.',
      'Apply filters to customize the report view.',
      'Save custom filter sets as personal or shared views.',
      'Export reports in Excel, PDF, or CSV format.',
    ],
    tips: [
      'Schedule reports to run automatically and email results.',
      'Pin frequently-used reports to your dashboard.',
      'Use the compare feature to see side-by-side data.',
      'Request new reports through the help center.',
    ],
    keywords: ['reports', 'reporting', 'dashboards', 'analytics', 'export', 'data', 'visualization'],
    modules: ['reporting'],
    relatedTopics: ['budget-vs-actual', 'dashboard'],
    cta: { label: 'Open Analytics BI', path: '/superset' },
    walkthroughId: 'analytics-bi',
  },

  // ============================================
  // ADMINISTRATION
  // ============================================
  {
    key: 'admin-guide',
    label: 'Administrator Guide',
    description: 'Configure system settings, users, and workflows.',
    category: 'Administration',
    steps: [
      'Access the Admin panel from the main navigation (requires admin role).',
      'Navigate through the admin sections: Users, Roles, Workflows, Settings.',
      'Configure system-wide settings in the Settings section.',
      'Manage user accounts and role assignments in Users.',
      'Set up approval workflows and routing rules.',
      'Monitor system activity through the audit log.',
    ],
    tips: [
      'Test workflow changes in a sandbox before applying to production.',
      'Document custom configurations for future reference.',
      'Set up admin notifications for critical system events.',
      'Regularly review user access and deactivate unused accounts.',
    ],
    keywords: ['admin', 'administration', 'settings', 'configuration', 'users', 'roles', 'setup'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['user-management', 'workflow-setup'],
    cta: { label: 'Open Admin Panel', path: '/admin' },
    walkthroughId: 'admin',
  },
  {
    key: 'user-management',
    label: 'User Management',
    description: 'Add, edit, and manage user accounts and permissions.',
    category: 'Administration',
    steps: [
      'Navigate to Admin > Users.',
      'View all users or search for a specific user.',
      'Click "Add User" to create a new account.',
      'Assign roles and department access.',
      'Set user status (active, inactive, locked).',
      'Users typically log in via SSO—manual passwords are optional.',
    ],
    tips: [
      'Use role templates for consistent permission assignment.',
      'Bulk import users from CSV for large batches.',
      'Deactivate rather than delete accounts to preserve audit history.',
      'Set up automatic account provisioning via SCIM if available.',
    ],
    keywords: ['users', 'accounts', 'permissions', 'roles', 'access', 'sso', 'management'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'role-management'],
    walkthroughId: 'user-management',
  },
  {
    key: 'workflow-setup',
    label: 'Workflow Configuration',
    description: 'Set up approval workflows and routing rules.',
    category: 'Administration',
    steps: [
      'Navigate to Admin > Workflows.',
      'View existing workflows or create a new one.',
      'Define workflow triggers (e.g., transfer over $10,000).',
      'Add approval steps and assign approvers.',
      'Configure routing logic (parallel, sequential, conditional).',
      'Test the workflow before activating.',
    ],
    tips: [
      'Document workflow logic for auditors and new admins.',
      'Set up escalation rules for overdue approvals.',
      'Use delegation features for approver absences.',
      'Monitor workflow performance and adjust as needed.',
    ],
    keywords: ['workflow', 'approval', 'routing', 'automation', 'rules', 'configuration'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'transfers-approve'],
    walkthroughId: 'workflow-setup',
  },
  {
    key: 'workflow-builder',
    label: 'Workflow Builder',
    description: 'Create and edit multi-step approval workflows.',
    category: 'Administration',
    steps: [
      'Open Admin > Workflow Builder.',
      'Create a new workflow and give it a clear name.',
      'Select the workflow to open the step editor.',
      'Add steps and assign approver roles or users.',
      'Configure SLA and escalation settings as needed.',
      'Reorder steps and save the workflow.',
      'Attach the workflow to forms or budget processes that require approvals.',
    ],
    tips: [
      'Use short, descriptive step names for audit logs.',
      'Require all approvers only when needed to avoid bottlenecks.',
      'Test the workflow with a sample request before go-live.',
      'Keep routing simple unless you have a specific policy requirement.',
    ],
    keywords: ['workflow', 'builder', 'approvals', 'steps', 'routing', 'sla', 'escalation'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['workflow-setup', 'form-builder'],
    cta: { label: 'Open Workflow Builder', path: '/admin/workflow-builder' },
    walkthroughId: 'workflow-builder',
  },
  {
    key: 'erp-etl',
    label: 'ERP ETL Console',
    description: 'Pull GL actuals from ERP and push budget lines back.',
    category: 'Administration',
    steps: [
      'Open Admin > ERP ETL.',
      'Review the connection status and latest accounting date.',
      'Use "Pull GL" with a date window and limit.',
      'Toggle Simulate to test with mock data when ERP is unavailable.',
      'Use "Push Budgets" with a scenario and fiscal year.',
      'Review results and refresh the status panel.',
    ],
    tips: [
      'Start with a small date window to validate mappings.',
      'Use dry run or simulate before enabling real pushes.',
      'Confirm chartfields and departments are in sync with ERP codes.',
      'Use the scheduler for automated nightly pulls.',
    ],
    keywords: ['erp', 'etl', 'gl', 'pull', 'push', 'integration', 'sync'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['erp-scheduler', 'data-import'],
    cta: { label: 'Open ERP ETL', path: '/admin/erp' },
    walkthroughId: 'erp-etl',
  },
  {
    key: 'erp-scheduler',
    label: 'ERP Scheduler',
    description: 'Automate ERP pulls and budget pushes on a schedule.',
    category: 'Administration',
    steps: [
      'Open Admin > ERP Scheduler.',
      'Create a job and choose an action (nightly, pull-only, push-only).',
      'Set the run time and frequency.',
      'Configure parameters such as pull window, replace, and push scenario.',
      'Save the job and click "Run now" to test.',
      'Review recent runs and reset or delete jobs as needed.',
    ],
    tips: [
      'Use simulate and dry run settings in non-production.',
      'Keep pull windows small while testing.',
      'Check run history for repeated failures before enabling.',
      'Scheduled jobs require the ERP runner to be active.',
    ],
    keywords: ['erp', 'scheduler', 'jobs', 'nightly', 'automation', 'sync'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['erp-etl'],
    cta: { label: 'Open ERP Scheduler', path: '/admin/erp/scheduler' },
  },
  {
    key: 'erp-connectors',
    label: 'ERP Connectors',
    description: 'Configure ERP connections, credentials, and sync entities.',
    category: 'Administration',
    steps: [
      'Open Admin > ERP Connectors.',
      'Create a connector and select the ERP type.',
      'Choose a connection mode (API, DB, or hybrid).',
      'Enter API or database credentials and test the connection.',
      'Select the entities you want to sync.',
      'Save the connector and run a pull to validate.',
    ],
    tips: [
      'Start with GL only to validate mappings and performance.',
      'Use simulate mode in non-production environments.',
      'Store credentials in secrets and rotate on schedule.',
      'Review sync logs for failures or skipped records.',
    ],
    keywords: ['erp', 'connector', 'banner', 'peoplesoft', 'api', 'db', 'sync', 'integration'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['erp-etl', 'erp-scheduler', 'erp-sync-logs'],
    cta: { label: 'Open ERP Connectors', path: '/admin/erp-connectors' },
  },
  {
    key: 'erp-sync-logs',
    label: 'ERP Sync Logs',
    description: 'Review recent ERP sync runs, statuses, and record counts.',
    category: 'Administration',
    steps: [
      'Open Admin > ERP Connectors.',
      'Switch to the Sync History tab.',
      'Review the timestamp, connector, and entity for each run.',
      'Check status chips for success, warning, or error.',
      'Open connector settings if failures repeat.',
    ],
    tips: [
      'Use refresh after running a pull or push.',
      'Compare records processed to expected volumes.',
      'Warnings typically indicate partial data or skips.',
      'Track failures to connector credentials or endpoint changes.',
    ],
    keywords: ['erp', 'sync', 'logs', 'history', 'status', 'errors'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['erp-connectors', 'erp-etl'],
    cta: { label: 'Open ERP Connectors', path: '/admin/erp-connectors' },
  },
  {
    key: 'data-import',
    label: 'Import Enrollment, COA, and Fee Data',
    description: 'Upload student enrollment, cost of attendance, and fee rate files from the admin console.',
    category: 'Administration',
    steps: [
      'Open Admin > Data Import.',
      'Choose the dataset type (Enrollment, Cost of Attendance, or Fee Rates).',
      'Select a CSV file and click "Validate" to preview rows and errors.',
      'Fix any validation errors in your file and re-validate.',
      'Click "Upload" to start the import and monitor progress.',
      'Review the summary counts after completion.',
    ],
    tips: [
      'Keep column headers consistent with the template you used to export.',
      'Use smaller files when testing to confirm data formatting.',
      'Imports run in the background; wait for the completion status.',
      'If an import fails, correct the source file and re-run validation.',
      'See /docs/data-import for the full walkthrough.',
    ],
    keywords: ['data import', 'enrollment', 'fees', 'cost of attendance', 'coa', 'upload', 'csv'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['transactions-import', 'reports'],
    cta: { label: 'Open Data Import', path: '/admin/data-import' },
    walkthroughId: 'data-import',
  },
  {
    key: 'transactions-import',
    label: 'Import Transactions',
    description: 'Map and load transaction files to power dashboards and reports.',
    category: 'Administration',
    steps: [
      'Open Admin > Transaction Import.',
      'Upload a CSV or Excel file.',
      'Map each column header to a destination field.',
      'Review the preview rows for formatting issues.',
      'Start the import and monitor the progress status.',
      'Save the mapping profile for future imports.',
    ],
    tips: [
      'Use signed amounts (positive for budget or credit, negative for expense or debit).',
      'Make sure department and account codes match your master data.',
      'Keep a default mapping profile for recurring imports.',
      'Large files may take a few minutes; wait for the completion status.',
    ],
    keywords: ['transactions', 'import', 'mapping', 'csv', 'xlsx', 'ledger', 'actuals'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['data-import', 'budget-vs-actual', 'reports'],
    cta: { label: 'Open Transaction Import', path: '/admin/transactions/import' },
  },
  {
    key: 'form-builder',
    label: 'Build Custom Forms',
    description: 'Create forms and tables without writing code.',
    category: 'Administration',
    steps: [
      'Open Admin > Form Builder.',
      'Set a form name, description, and target table (optional).',
      'Add fields and choose types and validation rules.',
      'Review field mappings if you are linking to a target table.',
      'Assign a workflow if submissions need approvals.',
      'Save the form and open it at /form/<table_name> to test.',
    ],
    tips: [
      'Use clear field names; they become column names in the data table.',
      'Start with required fields only, then iterate.',
      'Use Admin > Form Submissions to review incoming requests.',
      'Pair with Validation Rules for cross-field checks.',
      'See /docs/form-builder for the full walkthrough.',
    ],
    keywords: ['forms', 'form builder', 'workflow', 'requests', 'submission', 'custom form'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['validation-rules', 'workflow-setup'],
    cta: { label: 'Open Form Builder', path: '/admin/form-builder' },
    walkthroughId: 'form-builder',
  },
  {
    key: 'form-submissions',
    label: 'Review Form Submissions',
    description: 'Filter, review, and export incoming form submissions.',
    category: 'Administration',
    steps: [
      'Open Admin > Form Submissions.',
      'Filter by form ID, submitter, status, or date range.',
      'Open a submission to review details.',
      'Update status or notes as needed.',
      'Use Export CSV to download the filtered list.',
    ],
    tips: [
      'Use "Pending Only" to focus on items needing review.',
      'Share filtered views with the Share Link button.',
      'Check approval fields before marking submissions complete.',
      'Keep filters narrow for faster exports.',
    ],
    keywords: ['form submissions', 'forms', 'review', 'approve', 'export', 'status'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['form-builder', 'workflow-setup', 'form-submission-workflows'],
    cta: { label: 'Open Form Submissions', path: '/admin/form_submissions' },
    walkthroughId: 'form-submissions',
  },
  {
    key: 'form-submission-workflows',
    label: 'Form Submission Workflows',
    description: 'Route form submissions through approvals and track status.',
    category: 'Administration',
    steps: [
      'Create a form and assign a workflow in Admin > Form Builder.',
      'Submit a request from the form page.',
      'Review pending submissions in Admin > Form Submissions.',
      'Approve or reject submissions as they progress through steps.',
      'Use the workflow status page to view step history when needed.',
    ],
    tips: [
      'Keep workflows short to reduce approval bottlenecks.',
      'Use clear step names for audit trails.',
      'Share submission links with approvers who need context.',
      'Pair with validation rules for policy enforcement.',
    ],
    keywords: ['forms', 'workflow', 'approvals', 'submission', 'status', 'review'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['form-builder', 'workflow-builder', 'form-submissions'],
    cta: { label: 'Open Form Builder', path: '/admin/form-builder' },
    walkthroughId: 'form-submission-workflows',
  },
  {
    key: 'validation-rules',
    label: 'Configure Validation Rules',
    description: 'Enforce cross-field rules and policies on submissions.',
    category: 'Administration',
    steps: [
      'Open Admin > Validation Rules.',
      'Create or select a rule set for a scope (global or tenant).',
      'Add rules with an if field, match value, and required or prohibited field.',
      'Use the simulator to test sample payloads.',
      'Publish the rule set to make it active.',
    ],
    tips: [
      'Use wildcards like 5* or ranges like 100-200 in match values.',
      'Set severity to warn for soft policy checks.',
      'Keep rules small and focused to simplify troubleshooting.',
      'Document rule intent in the error message.',
    ],
    keywords: ['validation', 'rules', 'policy', 'forms', 'cross-field', 'required', 'prohibit'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['form-builder', 'data-import'],
    cta: { label: 'Open Validation Rules', path: '/admin/validation-rules' },
    walkthroughId: 'validation-rules',
  },
  {
    key: 'sso-settings',
    label: 'Configure SSO Settings',
    description: 'Review SAML and OIDC status and test your identity provider setup.',
    category: 'Administration',
    steps: [
      'Open Admin > Security > SSO Settings.',
      'Review the SAML and OIDC status indicators.',
      'Copy the Entity ID and ACS URL into your IdP configuration.',
      'Update your IdP metadata or certificates as needed.',
      'Use "Test Configuration" to validate SAML settings.',
    ],
    tips: [
      'Most SSO settings come from environment variables and require a restart.',
      'Use the metadata link to download the service provider config.',
      'Confirm required attributes (email, name) are released by the IdP.',
      'Review recent SSO users to verify logins are working.',
    ],
    keywords: ['sso', 'saml', 'oidc', 'login', 'identity provider', 'authentication'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['user-management', 'admin-guide'],
    cta: { label: 'Open SSO Settings', path: '/admin/sso-settings' },
  },

  // ============================================
  // ACCOUNT & SETTINGS
  // ============================================
  {
    key: 'account-settings',
    label: 'Account Settings',
    description: 'Update your profile, preferences, and security settings.',
    category: 'Account & Settings',
    steps: [
      'Click your profile avatar in the top-right corner.',
      'Select "Account Settings" from the menu.',
      'Update your display name and contact information.',
      'Choose your preferred theme (light, dark, or system).',
      'Configure notification preferences.',
      'Change your password if using local authentication.',
    ],
    tips: [
      'Enable email notifications for items requiring your attention.',
      'Dark mode reduces eye strain in low-light environments.',
      'Save your timezone setting for accurate scheduling.',
      'Review your active sessions and sign out remotely if needed.',
    ],
    keywords: ['account', 'settings', 'profile', 'preferences', 'theme', 'notifications', 'password'],
    relatedTopics: ['restart-tour', 'keyboard-shortcuts'],
    cta: { label: 'Open Account Settings', path: '/account' },
    walkthroughId: 'account-settings',
  },
  {
    key: 'restart-tour',
    label: 'Restart the Onboarding Tour',
    description: 'Launch the guided walkthrough again to refresh your knowledge.',
    category: 'Account & Settings',
    steps: [
      'Click your profile avatar in the top-right corner.',
      'Select "Account Settings" from the menu.',
      'Scroll to the "Getting Started" section.',
      'Click "Restart Tour" to launch the onboarding wizard.',
      'Follow the wizard to review key features based on your role.',
    ],
    tips: [
      'The tour content updates based on your current roles and permissions.',
      'Use the tour after major updates to learn about new features.',
      'Share the tour with new team members during onboarding.',
    ],
    keywords: ['restart', 'tour', 'walkthrough', 'onboarding', 'guide', 'tutorial', 'help'],
    relatedTopics: ['getting-started', 'account-settings'],
    cta: { label: 'Open Account Settings', path: '/account' },
  },

  // ============================================
  // DATA OPERATIONS
  // ============================================
  {
    key: 'batch-operations',
    label: 'Batch Operations',
    description: 'Process multiple items at once for efficiency.',
    category: 'Data Operations',
    steps: [
      'Navigate to a page with items to process (transfers, approvals, submissions).',
      'Use checkboxes to select multiple items.',
      'Click the batch action button in the toolbar.',
      'Choose the action to apply (approve, reject, export, delete).',
      'Confirm the batch operation in the dialog.',
      'Review the results summary for successes and failures.',
    ],
    tips: [
      'Use "Select All" to select all visible items on the current page.',
      'Filter items first to narrow down before batch selection.',
      'Failed items are reported individually for targeted follow-up.',
      'Batch operations respect your role permissions—you can only batch what you can individually do.',
    ],
    keywords: ['batch', 'bulk', 'multiple', 'mass', 'select all', 'operations', 'process'],
    relatedTopics: ['transfers-approve', 'form-submissions'],
    walkthroughId: 'batch-operations',
  },
  {
    key: 'export-import-formats',
    label: 'Export and Import Formats',
    description: 'Understand supported file formats for data export and import.',
    category: 'Data Operations',
    steps: [
      'Navigate to any page with exportable data.',
      'Click the export button (download icon) in the toolbar.',
      'Choose your preferred format: Excel (.xlsx), CSV, or PDF.',
      'For imports, download the template first to ensure correct columns.',
      'Upload your prepared file and validate before importing.',
    ],
    tips: [
      'Excel format preserves formatting, formulas, and multiple sheets.',
      'CSV is best for data processing and integration with other systems.',
      'PDF is ideal for sharing reports and printing.',
      'Large exports may be delivered via email to avoid timeout issues.',
    ],
    keywords: ['export', 'import', 'csv', 'excel', 'xlsx', 'pdf', 'download', 'upload', 'format'],
    relatedTopics: ['data-import', 'transactions-import', 'reports'],
    walkthroughId: 'export-import-formats',
  },
  {
    key: 'accessibility',
    label: 'Accessibility Features',
    description: 'Use Uni-Fund with assistive technologies and accessibility preferences.',
    category: 'Account & Settings',
    steps: [
      'Navigate using Tab to move between interactive elements.',
      'Press Enter or Space to activate buttons and links.',
      'Use arrow keys to navigate within menus, dropdowns, and tables.',
      'Screen readers announce all interactive elements and status changes.',
      'Enable high contrast mode in Account Settings for better visibility.',
      'Adjust text size using browser zoom (Cmd/Ctrl + or -).',
    ],
    tips: [
      'Skip links at the top of each page let you jump directly to main content.',
      'All images include descriptive alt text for screen readers.',
      'Focus indicators clearly show which element is currently selected.',
      'Color is never the only way information is conveyed—icons and text are always included.',
      'Keyboard shortcuts work without requiring a mouse.',
    ],
    keywords: ['accessibility', 'a11y', 'screen reader', 'keyboard', 'high contrast', 'wcag', 'ada'],
    relatedTopics: ['account-settings', 'keyboard-shortcuts'],
    cta: { label: 'Open Account Settings', path: '/account' },
    walkthroughId: 'accessibility',
  },

  // ============================================
  // TUITION & FEE PROJECTION
  // ============================================
  {
    key: 'fee-projection',
    label: 'Tuition and Fee Projections',
    description: 'Model tuition rates and project revenue under different scenarios.',
    category: 'Tuition & Enrollment',
    steps: [
      'Navigate to Tuition > Fee Projections from the main menu.',
      'Select the academic year to model.',
      'Enter proposed tuition rate changes by student type.',
      'Input enrollment assumptions for each category.',
      'View projected revenue by category and total.',
      'Compare multiple scenarios side-by-side.',
      'Generate board presentation materials.',
    ],
    tips: [
      'Model best-case and worst-case enrollment scenarios.',
      'Include fee waivers and discounts in your calculations.',
      'Link projections to your financial aid strategy.',
      'Export projections to Excel for further analysis.',
    ],
    keywords: ['tuition', 'fees', 'projection', 'revenue', 'enrollment', 'rates', 'modeling'],
    modules: ['enrollment'],
    relatedTopics: ['enrollment-funnel', 'enrollment-yield', 'budget-scenarios'],
    cta: { label: 'Open Fee Projections', path: '/tuition/projections' },
    walkthroughId: 'fee-projection',
  },

  // ============================================
  // USER SUBMISSIONS & WORKFLOW
  // ============================================
  {
    key: 'my-submissions',
    label: 'My Submissions',
    description: 'View and manage forms you have submitted through the system.',
    category: 'Forms & Submissions',
    steps: [
      'Navigate to Forms → My Submissions from the sidebar.',
      'View all your submitted forms with current status.',
      'Use status filter tabs (Draft, Pending, Approved, Rejected).',
      'Search submissions by title or form name.',
      'Click on a submission to view details.',
      'Withdraw pending submissions if needed.',
    ],
    tips: [
      'Save drafts to complete submissions later.',
      'Check status badges to track submission progress.',
      'Withdrawn submissions can be resubmitted after editing.',
      'Use filters to find specific submissions quickly.',
    ],
    keywords: ['submissions', 'forms', 'my submissions', 'draft', 'pending', 'withdraw', 'status'],
    relatedTopics: ['accessible-submissions', 'workflow-status', 'form-builder'],
    cta: { label: 'View My Submissions', path: '/forms/my-submissions' },
    walkthroughId: 'my-submissions',
  },
  {
    key: 'accessible-submissions',
    label: 'Accessible Submissions',
    description: 'Review submissions you have access to based on your role and permissions.',
    category: 'Forms & Submissions',
    steps: [
      'Navigate to Forms → Accessible Submissions.',
      'View submissions from your departments and areas.',
      'Filter by status using the tabs at the top.',
      'Search across all accessible submissions.',
      'Click a submission to review details.',
      'Take action on submissions you are authorized to process.',
    ],
    tips: [
      'Access is determined by role and department assignments.',
      'Use this view to monitor team submission activity.',
      'Bulk actions can process multiple submissions at once.',
      'Export submission data for reporting purposes.',
    ],
    keywords: ['submissions', 'accessible', 'review', 'permissions', 'department', 'team'],
    relatedTopics: ['my-submissions', 'workflow-status', 'approval-queue'],
    cta: { label: 'View Accessible Submissions', path: '/forms/accessible-submissions' },
    walkthroughId: 'accessible-submissions',
  },
  {
    key: 'workflow-status',
    label: 'Workflow Status',
    description: 'Track items through approval workflows with AI-powered status explanations.',
    category: 'Forms & Submissions',
    steps: [
      'Navigate to Workflow → Status from the sidebar.',
      'View all items currently in approval workflows.',
      'See current step and assigned approver for each item.',
      'Click "Why is this pending?" for AI explanation.',
      'Filter by workflow type, status, or date.',
      'View complete approval history for any item.',
    ],
    tips: [
      'The AI feature analyzes workflow rules to explain status.',
      'Set up notifications for workflow events.',
      'Use timeline view to see approval progression.',
      'Export workflow reports for auditing.',
    ],
    keywords: ['workflow', 'status', 'pending', 'approval', 'AI', 'tracking', 'history'],
    relatedTopics: ['approval-queue', 'workflow-builder', 'my-submissions'],
    cta: { label: 'View Workflow Status', path: '/workflow' },
    walkthroughId: 'workflow-status',
  },

  // ============================================
  // DEPARTMENT & PERSONNEL DETAILS
  // ============================================
  {
    key: 'department-detail',
    label: 'Department Detail View',
    description: 'Deep-dive into individual department budgets, positions, and funding.',
    category: 'Department Overview',
    steps: [
      'Navigate to a department from Department Overview.',
      'View budget summary with variance indicators.',
      'Review the PCN table showing all positions.',
      'Explore the Routing section for fund flows.',
      'Click positions to see allocation details.',
      'Access funding tables for revenue and expenditures.',
      'Use quick actions for transfers and reports.',
    ],
    tips: [
      'Variance percentages highlight areas needing attention.',
      'Sort and filter the PCN table by various attributes.',
      'Export department data for offline analysis.',
      'Use breadcrumbs to navigate the department hierarchy.',
    ],
    keywords: ['department', 'detail', 'budget', 'positions', 'PCN', 'routing', 'funding'],
    relatedTopics: ['department-overview', 'personnel-detail', 'budget-transfers'],
    cta: { label: 'View Departments', path: '/departments' },
    walkthroughId: 'department-detail',
  },
  {
    key: 'personnel-detail',
    label: 'Personnel Detail View',
    description: 'View individual employee allocations, compensation, and funding distribution.',
    category: 'Position Management',
    steps: [
      'Click a position from department view or search.',
      'View employee information header with status.',
      'Review funding allocations across sources.',
      'Explore What-If scenarios for compensation changes.',
      'Access transfer history for past movements.',
      'Use Compensation Adjustment for salary modeling.',
      'Review effort distribution across projects.',
    ],
    tips: [
      'Allocations must total 100% across all sources.',
      'What-If scenarios don\'t affect actual data until approved.',
      'Document allocation change reasons in notes.',
      'Historical data shows funding patterns over time.',
    ],
    keywords: ['personnel', 'employee', 'allocation', 'compensation', 'salary', 'effort', 'funding'],
    modules: ['positions'],
    relatedTopics: ['department-detail', 'positions', 'personnel-whatif'],
    cta: { label: 'View Position Budgeting', path: '/positions' },
    walkthroughId: 'personnel-detail',
  },

  // ============================================
  // CAPITAL PROJECT DETAILS
  // ============================================
  {
    key: 'capital-project-detail',
    label: 'Capital Project Detail',
    description: 'Comprehensive view of capital project information, budget, and timeline.',
    category: 'Capital Planning',
    steps: [
      'Navigate to Capital → Projects and select a project.',
      'View project header with status and budget summary.',
      'Use Overview tab for high-level KPIs.',
      'Review Timeline tab for milestones.',
      'Access Budget tab for cost breakdown.',
      'Check Funding tab for sources and draws.',
      'Manage files in the Documents tab.',
    ],
    tips: [
      'Status indicators show project health at a glance.',
      'Budget variance alerts highlight cost overruns early.',
      'Milestone tracking identifies schedule risks.',
      'Keep all project documentation organized.',
    ],
    keywords: ['capital', 'project', 'detail', 'budget', 'timeline', 'milestones', 'funding'],
    modules: ['capital'],
    relatedTopics: ['capital-projects', 'capital-change-orders', 'capital-phases'],
    cta: { label: 'View Capital Projects', path: '/capital/projects' },
    walkthroughId: 'capital-project-detail',
  },
  {
    key: 'capital-change-orders',
    label: 'Capital Change Orders',
    description: 'Manage change orders for capital projects with full approval tracking.',
    category: 'Capital Planning',
    steps: [
      'Open a capital project and go to Change Orders tab.',
      'View all change orders with status and amounts.',
      'Click Add Change Order to create a request.',
      'Enter scope, cost impact, and justification.',
      'Submit for approval through the workflow.',
      'Track approval status and view history.',
    ],
    tips: [
      'Document justification thoroughly for audits.',
      'Include timeline impact when applicable.',
      'Attach supporting documents for approvers.',
      'Large change orders may need board approval.',
    ],
    keywords: ['capital', 'change order', 'CO', 'approval', 'budget', 'modification'],
    modules: ['capital'],
    relatedTopics: ['capital-project-detail', 'capital-phases', 'approval-queue'],
    cta: { label: 'View Capital Projects', path: '/capital/projects' },
    walkthroughId: 'capital-change-orders',
  },
  {
    key: 'capital-phases',
    label: 'Capital Project Phases',
    description: 'Organize capital projects into phases with individual budgets and timelines.',
    category: 'Capital Planning',
    steps: [
      'Open a capital project and go to Phases tab.',
      'View phases with budget allocations and timelines.',
      'Click a phase to see detailed breakdown.',
      'Track completion percentage vs planned spend.',
      'Update phase status as work progresses.',
      'Review phase-level funding sources.',
    ],
    tips: [
      'Phases organize large projects into segments.',
      'Each phase can have its own budget and timeline.',
      'Phase milestones provide granular progress tracking.',
      'Phase reports give detailed project visibility.',
    ],
    keywords: ['capital', 'phases', 'timeline', 'milestones', 'budget', 'progress'],
    modules: ['capital'],
    relatedTopics: ['capital-project-detail', 'capital-change-orders', 'capital-contractors'],
    cta: { label: 'View Capital Projects', path: '/capital/projects' },
    walkthroughId: 'capital-phases',
  },
  {
    key: 'capital-contractors',
    label: 'Capital Contractor Management',
    description: 'Manage contractors, contracts, and payments for capital projects.',
    category: 'Capital Planning',
    steps: [
      'Open a capital project and go to Contractors tab.',
      'View all contractors assigned to the project.',
      'Add new contractors with contract details.',
      'Review amounts, payment terms, and status.',
      'Track payments and retainage.',
      'Manage contractor documents and certifications.',
    ],
    tips: [
      'Keep insurance certificates current.',
      'Track retainage for proper release timing.',
      'Document all contract modifications.',
      'Use payment history for cash flow planning.',
    ],
    keywords: ['capital', 'contractors', 'contracts', 'payments', 'retainage', 'insurance'],
    modules: ['capital'],
    relatedTopics: ['capital-project-detail', 'capital-phases', 'capital-change-orders'],
    cta: { label: 'View Capital Projects', path: '/capital/projects' },
    walkthroughId: 'capital-contractors',
  },
  {
    key: 'capital-space-impact',
    label: 'Capital Space Impact Analysis',
    description: 'Analyze how capital projects affect space allocations and metrics.',
    category: 'Capital Planning',
    steps: [
      'Open a capital project and go to Space Impact tab.',
      'View current and projected space metrics.',
      'Review assignable square footage changes.',
      'See impact on department space allocations.',
      'Model different space scenarios.',
      'Link changes to facilities management.',
    ],
    tips: [
      'Space metrics affect indirect cost calculations.',
      'Document space classification for compliance.',
      'Consider multi-use space designations.',
      'Coordinate with facilities for accurate data.',
    ],
    keywords: ['capital', 'space', 'ASF', 'square footage', 'facilities', 'F&A'],
    modules: ['capital'],
    relatedTopics: ['capital-project-detail', 'capital-planning'],
    cta: { label: 'View Capital Projects', path: '/capital/projects' },
    walkthroughId: 'capital-space-impact',
  },

  // ============================================
  // ADMIN DATA MANAGEMENT
  // ============================================
  {
    key: 'admin-tree-management',
    label: 'Organization Tree Management',
    description: 'Manage hierarchical structures for departments, budgets, and reporting.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Organization Trees.',
      'Select tree type (Department, Budget, Reporting).',
      'View hierarchy with expand/collapse controls.',
      'Drag and drop nodes to reorganize.',
      'Right-click nodes for add, edit, delete actions.',
      'Use effective date for scheduled changes.',
    ],
    tips: [
      'Schedule changes for future effective dates.',
      'Preview impacts before saving changes.',
      'Export tree structure for documentation.',
      'Search to find nodes in large trees.',
    ],
    keywords: ['admin', 'tree', 'hierarchy', 'organization', 'department', 'structure'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'department-overview'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-tree-management',
  },
  {
    key: 'admin-budget-drivers',
    label: 'Budget Driver Configuration',
    description: 'Configure budget drivers for automated allocation calculations.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Budget Drivers.',
      'View configured drivers (enrollment, credit hours, etc.).',
      'Click Add Driver to create new driver.',
      'Configure formula and data sources.',
      'Set weights and allocation rules.',
      'Test calculations with sample data.',
    ],
    tips: [
      'Drivers automate budget allocations.',
      'Multiple drivers can be weighted.',
      'Historical data supports trend analysis.',
      'Document driver logic for audit trails.',
    ],
    keywords: ['admin', 'drivers', 'allocation', 'formula', 'budget', 'calculation'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'budget-model'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-budget-drivers',
  },
  {
    key: 'admin-notifications',
    label: 'Notification Management',
    description: 'Configure notification templates, channels, and delivery rules.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Notification Settings.',
      'View templates organized by event type.',
      'Edit templates to customize content.',
      'Configure channels (email, in-app, SMS).',
      'Set up notification rules and triggers.',
      'Test with preview functionality.',
    ],
    tips: [
      'Use merge fields to personalize messages.',
      'Set frequency to avoid notification fatigue.',
      'Different channels suit different urgency.',
      'Review delivery reports for issues.',
    ],
    keywords: ['admin', 'notifications', 'email', 'templates', 'alerts', 'channels'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'account-settings'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-notifications',
  },
  {
    key: 'admin-report-scheduling',
    label: 'Report Scheduling',
    description: 'Set up automated report generation and distribution.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Scheduled Reports.',
      'View scheduled reports with run times.',
      'Click Add Schedule for new recurring report.',
      'Select report template and parameters.',
      'Configure frequency and distribution list.',
      'Set output format and delivery method.',
    ],
    tips: [
      'Schedule during off-peak hours.',
      'Use distribution lists for consistency.',
      'Archive reports for compliance.',
      'Monitor failed deliveries in logs.',
    ],
    keywords: ['admin', 'reports', 'scheduling', 'automation', 'distribution', 'recurring'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'reports'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-report-scheduling',
  },
  {
    key: 'admin-bulk-transfers',
    label: 'Bulk Transfer Operations',
    description: 'Process multiple budget transfers at once using templates.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Bulk Operations → Transfers.',
      'Download the bulk transfer template.',
      'Fill in transfer details following format.',
      'Upload completed file.',
      'Review validation results and fix errors.',
      'Submit batch for processing and approval.',
    ],
    tips: [
      'Validate data before uploading.',
      'Use template exactly as provided.',
      'Large batches process in chunks.',
      'Keep original files for audit purposes.',
    ],
    keywords: ['admin', 'bulk', 'transfers', 'batch', 'import', 'template'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'budget-transfers', 'data-import'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-bulk-transfers',
  },
  {
    key: 'admin-security',
    label: 'Security Settings',
    description: 'Configure role-based access control, row-level security, and SSO.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Security Settings.',
      'Review user roles and permissions.',
      'Configure RBAC rules.',
      'Set up row-level security for data isolation.',
      'Review audit logs for monitoring.',
      'Configure SSO and authentication.',
    ],
    tips: [
      'Use role inheritance for simplicity.',
      'Review access regularly for compliance.',
      'Enable audit logging for sensitive ops.',
      'Test permission changes with test user.',
    ],
    keywords: ['admin', 'security', 'RBAC', 'permissions', 'SSO', 'audit', 'roles'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'user-management', 'role-management'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'admin-security',
  },
  {
    key: 'data-import-export',
    label: 'Data Import & Export',
    description: 'Import and export data with templates and validation.',
    category: 'Administration',
    steps: [
      'Navigate to Admin → Data Import/Export.',
      'Select data type to import or export.',
      'For imports, download and fill template.',
      'Upload files and review validation.',
      'For exports, configure filters and format.',
      'Schedule recurring operations if needed.',
    ],
    tips: [
      'Use CSV format for best compatibility.',
      'Validate before large imports.',
      'Archive exports for compliance.',
      'Set up automated integrations.',
    ],
    keywords: ['admin', 'import', 'export', 'data', 'CSV', 'template', 'validation'],
    roles: ROLE_GROUPS.ADMIN,
    relatedTopics: ['admin-guide', 'data-import', 'transactions-import'],
    cta: { label: 'Open Admin', path: '/admin' },
    walkthroughId: 'data-import-export',
  },

  // ============================================
  // USER ROLES & PERMISSIONS
  // ============================================
  {
    key: 'user-roles-overview',
    label: 'Understanding User Roles & Permissions',
    description: 'Learn how roles and permissions control access in Uni-Fund.',
    category: 'User Roles & Permissions',
    steps: [
      'Roles define what a user can do in Uni-Fund (e.g., admin, finance, dept_manager).',
      'Each role has a set of permissions that grant access to specific actions.',
      'Users can have multiple roles, and their permissions are combined.',
      'Roles can be "Global" (access all departments) or "Department Scoped" (limited to assigned departments).',
      'Permissions follow a resource.action pattern (e.g., budget_requests.approve, personnel.view).',
    ],
    tips: [
      'Users inherit all permissions from all their assigned roles.',
      'The admin and superadmin roles have full access to all features.',
      'Department-scoped roles are ideal for managers who should only see their own departments.',
      'Use the Role Permission Matrix in Admin to see all role-permission mappings at a glance.',
    ],
    keywords: ['roles', 'permissions', 'access', 'RBAC', 'authorization', 'security', 'user management'],
    relatedTopics: ['creating-custom-roles', 'assigning-roles', 'department-scoping', 'permissions-reference'],
    cta: { label: 'Manage Roles', path: '/admin#/roles' },
    roles: ROLE_GROUPS.ADMIN,
  },
  {
    key: 'creating-custom-roles',
    label: 'Creating Custom Roles',
    description: 'Step-by-step guide to creating a new role with specific permissions.',
    category: 'User Roles & Permissions',
    steps: [
      'Navigate to Admin → Roles.',
      'Click the "Create" button to add a new role.',
      'Enter a Name (e.g., "Research Coordinator") - a slug will be auto-generated.',
      'Add a Description to help others understand the role\'s purpose.',
      'Toggle "Restrict to Assigned Departments" ON if this role should only see their own departments.',
      'Click Save - you\'ll be redirected to assign permissions.',
      'In the Permissions section, expand each resource category (Budget, Personnel, etc.).',
      'Check the permissions this role should have (or use "Grant All" for a category).',
      'Permissions save automatically as you toggle them.',
    ],
    tips: [
      'Start with minimal permissions and add more as needed (principle of least privilege).',
      'Use descriptive role names that clearly indicate the role\'s function.',
      'Copy an existing similar role\'s permissions as a starting point.',
      'Test the new role by assigning it to a test user and verifying access.',
      'Document the role\'s intended use in the Description field.',
    ],
    keywords: ['create role', 'new role', 'custom role', 'role setup', 'permissions', 'RBAC'],
    relatedTopics: ['user-roles-overview', 'assigning-roles', 'permissions-reference'],
    cta: { label: 'Create New Role', path: '/admin#/roles/create' },
    roles: ROLE_GROUPS.ADMIN,
  },
  {
    key: 'assigning-roles',
    label: 'Assigning Roles to Users',
    description: 'How to assign one or more roles to a user account.',
    category: 'User Roles & Permissions',
    steps: [
      'Navigate to Admin → Users.',
      'Find the user you want to modify (use the search box if needed).',
      'Click the Edit button on the user\'s row.',
      'Scroll to the "Roles" field - this is a multi-select dropdown.',
      'Select one or more roles to assign to the user.',
      'If assigning a department-scoped role, also assign the user to specific Departments.',
      'Click Save to apply the changes.',
      'The user\'s access will update immediately (they may need to refresh their browser).',
    ],
    tips: [
      'Users can have multiple roles - permissions are combined from all assigned roles.',
      'Remove roles a user no longer needs to follow least privilege principles.',
      'When a user changes departments, remember to update their department assignments too.',
      'Use the Sudo feature to impersonate a user and verify their access level.',
      'Changes to roles are logged in the audit trail for compliance tracking.',
    ],
    keywords: ['assign role', 'user role', 'add role', 'user permissions', 'access control'],
    relatedTopics: ['user-roles-overview', 'department-scoping', 'creating-custom-roles'],
    cta: { label: 'Manage Users', path: '/admin#/users' },
    roles: ROLE_GROUPS.ADMIN,
  },
  {
    key: 'department-scoping',
    label: 'Department Scoping Explained',
    description: 'Control which departments a user can access based on their role.',
    category: 'User Roles & Permissions',
    steps: [
      'Department scoping restricts a role to only see data from assigned departments.',
      'When creating or editing a role, toggle "Restrict to Assigned Departments" to enable scoping.',
      'Assign users to their specific departments via Admin → Users → Edit User → Departments.',
      'A department-scoped user will only see budget, personnel, and other data from their departments.',
      'Global roles (like admin, finance) see all departments regardless of assignments.',
    ],
    tips: [
      'Use department-scoped roles for: dept_manager, course_planner, hr staff, etc.',
      'A user can be assigned to multiple departments and will see data from all of them.',
      'If a user needs to see all departments, either assign a global role or assign them to all departments.',
      'Department scoping applies to most data views, reports, and approval queues.',
      'Workflows can route approvals to users based on their department assignments.',
    ],
    keywords: ['department', 'scoping', 'data access', 'department manager', 'isolation', 'multi-department'],
    relatedTopics: ['user-roles-overview', 'assigning-roles', 'creating-custom-roles'],
    cta: { label: 'Edit Roles', path: '/admin#/roles' },
    roles: ROLE_GROUPS.ADMIN,
  },
  {
    key: 'permissions-reference',
    label: 'Permissions Reference Guide',
    description: 'Complete reference of all available permissions and what they control.',
    category: 'User Roles & Permissions',
    steps: [
      'Permissions follow a resource.action naming convention (e.g., budget_requests.approve).',
      'Common actions include: view, create, edit, delete, approve, export.',
      'Navigate to Admin → Roles → Edit any role to see all available permissions.',
      'Permissions are grouped by resource (Budget, Personnel, Positions, Forms, etc.).',
      'The admin.all wildcard permission grants complete access to everything.',
    ],
    tips: [
      'View permissions: Allow seeing data but not modifying it.',
      'Edit permissions: Allow modifying existing records.',
      'Create permissions: Allow adding new records.',
      'Approve permissions: Allow approving workflow items.',
      'Export permissions: Allow downloading data as Excel/CSV.',
      'Delete permissions: Allow removing records (use sparingly).',
    ],
    keywords: ['permissions', 'reference', 'actions', 'resources', 'view', 'edit', 'approve', 'delete'],
    relatedTopics: ['user-roles-overview', 'creating-custom-roles', 'admin-security'],
    cta: { label: 'View Permission Matrix', path: '/admin/permissions' },
    roles: ROLE_GROUPS.ADMIN,
  },
  {
    key: 'navigation-restrictions',
    label: 'Restricting Users to Specific Pages',
    description: 'How to limit which pages users with a role can see and access.',
    category: 'User Roles & Permissions',
    steps: [
      'Navigate to Admin → Roles and edit the role you want to restrict.',
      'Scroll down to the "Navigation Restrictions" section.',
      'Toggle ON "Restrict navigation to specific pages".',
      'Select the pages this role should be able to access.',
      'Unselected pages will be hidden from navigation and blocked if accessed directly.',
      'Changes save automatically as you toggle pages.',
    ],
    tips: [
      'This feature works with ANY role - not just specific examples like PI.',
      'Combined with department scoping, you can create very targeted access.',
      'Admin and superadmin roles cannot have navigation restrictions (they need full access).',
      'Users will always have access to Help, Account Settings, and Login/Logout.',
      'If a parent route is selected (e.g., /research), child routes are also accessible.',
      'Test the role by assigning it to a test user and logging in as them.',
    ],
    keywords: ['navigation', 'pages', 'restrict', 'hide', 'menu', 'access', 'single page', 'limit'],
    relatedTopics: ['pi-role-example', 'creating-custom-roles', 'user-roles-overview'],
    cta: { label: 'Edit Roles', path: '/admin#/roles' },
    roles: ROLE_GROUPS.ADMIN,
    walkthroughId: 'navigation-restrictions',
  },
  {
    key: 'pi-role-example',
    label: 'Example: Creating a PI (Principal Investigator) Role',
    description: 'Step-by-step example of creating a restricted role for research PIs who only see their portfolio.',
    category: 'User Roles & Permissions',
    steps: [
      'Navigate to Admin → Roles and click Create.',
      'Name: "Principal Investigator" (slug will be: principal_investigator).',
      'Description: "Research PI with view-only access to their research portfolio".',
      'Toggle "Restrict to Assigned Departments" to ON.',
      'Click Save to create the role.',
      'In the Permissions section, grant ONLY these permissions:',
      '  - research.view - View research awards and commitments',
      '  - research_portfolio.view - View portfolio dashboard',
      '  - personnel.view - View personnel on their awards',
      'Leave all other permissions unchecked.',
      'Scroll down to Navigation Restrictions section.',
      'Toggle ON "Restrict navigation to specific pages".',
      'Select ONLY: Research Portfolio (and optionally Research Awards if they need it).',
      'This ensures PIs only see the Research Portfolio page when they log in.',
      'Assign this role to your PI users via Admin → Users.',
      'Assign each PI to their specific department(s).',
    ],
    tips: [
      'This creates a read-only view for PIs to see their research data.',
      'PIs will only see research awards associated with their assigned departments.',
      'Add research.edit permission if PIs should be able to update award details.',
      'With navigation restrictions, PIs will ONLY see the Research Portfolio in their menu.',
      'Consider creating separate roles for "PI View Only" vs "PI with Edit Access".',
      'PIs can still access Help and Account Settings even with navigation restrictions.',
      'This same pattern works for ANY role type - Finance, HR, Department Heads, etc.',
    ],
    keywords: ['PI', 'principal investigator', 'research', 'example', 'restricted access', 'read only', 'single page'],
    relatedTopics: ['creating-custom-roles', 'department-scoping', 'user-roles-overview', 'navigation-restrictions'],
    cta: { label: 'Create PI Role', path: '/admin#/roles/create' },
    roles: ROLE_GROUPS.ADMIN,
    walkthroughId: 'pi-role-setup',
  },
  {
    key: 'role-best-practices',
    label: 'Role Management Best Practices',
    description: 'Guidelines for designing and managing roles effectively.',
    category: 'User Roles & Permissions',
    steps: [
      'Follow the Principle of Least Privilege: Give users only the access they need.',
      'Create functional roles based on job responsibilities, not individual people.',
      'Use department scoping for roles that should only see their own data.',
      'Document each role\'s purpose in the Description field.',
      'Review and audit role assignments quarterly for compliance.',
      'Test new roles with a test user before rolling out to real users.',
    ],
    tips: [
      'Avoid creating too many granular roles - aim for 10-15 well-defined roles.',
      'Name roles consistently (e.g., resource_action pattern like "budget_approver").',
      'Use role groups for common patterns (all finance roles share certain permissions).',
      'Keep the admin role for system configuration only, not daily operations.',
      'Remove roles from users who leave or change positions promptly.',
      'Use the audit log to track who made role/permission changes.',
    ],
    keywords: ['best practices', 'role design', 'security', 'governance', 'audit', 'compliance'],
    relatedTopics: ['user-roles-overview', 'creating-custom-roles', 'admin-security'],
    cta: { label: 'Review Roles', path: '/admin#/roles' },
    roles: ROLE_GROUPS.ADMIN,
  },
];

/**
 * FAQ content for the Help Center
 */
export const FAQ_ITEMS = [
  {
    question: 'How do I reset my password?',
    answer: 'If your institution uses Single Sign-On (SSO), password resets are handled through your institution\'s identity provider. For local accounts, go to Account Settings and click "Change Password." If you\'re locked out, contact your system administrator.',
    category: 'Account',
  },
  {
    question: 'Why can\'t I see certain menu items?',
    answer: 'Menu visibility is based on your assigned roles and permissions. If you need access to additional features, contact your department administrator or system admin to request the appropriate role.',
    category: 'Access',
  },
  {
    question: 'What is the difference between Global and Department-Scoped roles?',
    answer: 'Global roles (like admin, finance, controller) can see data from all departments. Department-scoped roles (like dept_manager, course_planner) can only see data from departments they are explicitly assigned to. This allows you to give department managers access only to their own department\'s budget and personnel data.',
    category: 'Access',
  },
  {
    question: 'How do I create a custom role for a specific user type?',
    answer: 'Go to Admin → Roles → Create. Give the role a descriptive name, set whether it should be department-scoped, then assign specific permissions. After saving, you can assign users to the new role via Admin → Users. See the "Creating Custom Roles" help topic for detailed steps.',
    category: 'Access',
  },
  {
    question: 'Can I restrict a user to only see one page in the app?',
    answer: 'Yes! When editing a role, scroll to "Navigation Restrictions" and enable "Restrict navigation to specific pages". Select only the pages you want the role to access. Unselected pages will be hidden from navigation and blocked if accessed directly. This is perfect for creating roles like "PI" that should only see the Research Portfolio page.',
    category: 'Access',
  },
  {
    question: 'How do I give a PI (Principal Investigator) access to view their research portfolio?',
    answer: 'Create a new role called "Principal Investigator" with department scoping enabled. Grant only research.view and research_portfolio.view permissions. Assign the PI to this role and to their specific department. They will only see research data from their assigned department. See the "PI Role Example" help topic for step-by-step instructions.',
    category: 'Access',
  },
  {
    question: 'How do budget transfers work?',
    answer: 'Budget transfers move funds between accounts, departments, or cost centers. You create a transfer request specifying source, destination, and amount. The request then goes through an approval workflow based on your institution\'s policies. Once approved, the transfer is applied to both accounts.',
    category: 'Budget',
  },
  {
    question: 'Can I undo a submitted request?',
    answer: 'Pending requests can be recalled before approval. Go to the request, click "Recall" or "Cancel." Once a request is approved and processed, it cannot be undone—you would need to create a reversing entry or new request.',
    category: 'Workflow',
  },
  {
    question: 'How often is data refreshed?',
    answer: 'Dashboard metrics and budget data are typically refreshed every 15-30 minutes. Real-time data (like approvals) updates immediately. The last refresh timestamp is shown on relevant pages. You can manually refresh by clicking the refresh icon.',
    category: 'Data',
  },
  {
    question: 'What browsers are supported?',
    answer: 'Uni-Fund supports the latest versions of Chrome, Firefox, Safari, and Edge. For the best experience, we recommend Chrome or Firefox. Internet Explorer is not supported.',
    category: 'Technical',
  },
  {
    question: 'How do I export data?',
    answer: 'Most pages with data tables have an export button (download icon). Click it to download data in Excel (.xlsx), CSV, or PDF format. For large exports, you may receive an email with a download link.',
    category: 'Data',
  },
  {
    question: 'Who do I contact for help?',
    answer: 'For application questions, use the Help Center or contact your department administrator. For technical issues, contact your IT help desk. For training, check if your institution offers Uni-Fund training sessions.',
    category: 'Support',
  },
  {
    question: 'How do I request a new feature?',
    answer: 'Feature requests can be submitted through the Help Center contact form. Describe your need and use case. Requests are reviewed periodically and prioritized based on impact and feasibility.',
    category: 'Support',
  },
  {
    question: 'What happens when the fiscal year changes?',
    answer: 'At fiscal year-end, your administrator will create the new fiscal year and copy forward relevant budgets and positions. You\'ll see both years during the transition period. Ensure you\'re working in the correct fiscal year by checking the year selector.',
    category: 'Budget',
  },
];

/**
 * Video tutorial content
 */
export const VIDEO_TUTORIALS = [
  {
    id: 'getting-started-video',
    title: 'Getting Started with Uni-Fund',
    description: 'A complete walkthrough of the Uni-Fund interface and core features.',
    duration: '5:30',
    thumbnail: '/assets/videos/thumbnails/getting-started.jpg',
    videoUrl: '/assets/videos/getting-started.mp4',
    category: 'Getting Started',
  },
  {
    id: 'global-search-video',
    title: 'Mastering Global Search',
    description: 'Learn to use Cmd+K to navigate quickly and find anything.',
    duration: '2:15',
    thumbnail: '/assets/videos/thumbnails/search.jpg',
    videoUrl: '/assets/videos/search-tutorial.mp4',
    category: 'Getting Started',
  },
  {
    id: 'budget-transfers-video',
    title: 'Creating Budget Transfers',
    description: 'Step-by-step guide to moving funds between accounts.',
    duration: '4:45',
    thumbnail: '/assets/videos/thumbnails/transfers.jpg',
    videoUrl: '/assets/videos/transfers-tutorial.mp4',
    category: 'Budget Management',
  },
  {
    id: 'position-budgeting-video',
    title: 'Position Budgeting Basics',
    description: 'How to create and manage position budgets effectively.',
    duration: '6:00',
    thumbnail: '/assets/videos/thumbnails/positions.jpg',
    videoUrl: '/assets/videos/positions-tutorial.mp4',
    category: 'Position Management',
  },
  {
    id: 'approval-workflows-video',
    title: 'Understanding Approval Workflows',
    description: 'Learn how requests move through the approval process.',
    duration: '3:30',
    thumbnail: '/assets/videos/thumbnails/workflows.jpg',
    videoUrl: '/assets/videos/workflows-tutorial.mp4',
    category: 'Workflow',
  },
  {
    id: 'reports-dashboards-video',
    title: 'Using Reports and Dashboards',
    description: 'Navigate reports, apply filters, and export data.',
    duration: '4:00',
    thumbnail: '/assets/videos/thumbnails/reports.jpg',
    videoUrl: '/assets/videos/reports-tutorial.mp4',
    category: 'Reports & Analytics',
  },
];

/**
 * Filter help topics based on user roles and enabled modules
 */
export function getHelpTopics({ userRoles = [], isModuleEnabled = () => false }) {
  const normalizedRoles = userRoles.map(r => String(r).toLowerCase());
  const isAdmin = hasRole(normalizedRoles, ROLE_GROUPS.ADMIN);

  return HELP_TOPICS.filter((topic) => {
    // Check role restrictions
    if (topic.roles && !hasRole(normalizedRoles, topic.roles)) return false;
    // Check module restrictions
    if (topic.modules && !hasModule(isModuleEnabled, topic.modules, isAdmin)) return false;
    return true;
  });
}

/**
 * Get a single topic by key
 */
export function getTopicByKey(key) {
  return HELP_TOPICS.find(t => t.key === key) || null;
}

/**
 * Get topics by category
 */
export function getTopicsByCategory(category, { userRoles = [], isModuleEnabled = () => false } = {}) {
  const allTopics = getHelpTopics({ userRoles, isModuleEnabled });
  return allTopics.filter(t => t.category === category);
}

/**
 * Get all unique categories
 */
export function getCategories({ userRoles = [], isModuleEnabled = () => false } = {}) {
  const allTopics = getHelpTopics({ userRoles, isModuleEnabled });
  const categories = [...new Set(allTopics.map(t => t.category))];
  return categories;
}

/**
 * Search topics
 */
export function searchTopics(query, { userRoles = [], isModuleEnabled = () => false } = {}) {
  const allTopics = getHelpTopics({ userRoles, isModuleEnabled });
  const lowerQuery = query.toLowerCase();

  return allTopics.filter(topic => {
    const searchableText = [
      topic.label,
      topic.description,
      ...(topic.keywords || []),
      ...(topic.steps || []),
      ...(topic.tips || []),
    ].join(' ').toLowerCase();

    return searchableText.includes(lowerQuery);
  });
}

/**
 * Get FAQ items by category
 */
export function getFaqByCategory(category) {
  if (!category) return FAQ_ITEMS;
  return FAQ_ITEMS.filter(faq => faq.category === category);
}

/**
 * Get video tutorials by category
 */
export function getVideosByCategory(category) {
  if (!category) return VIDEO_TUTORIALS;
  return VIDEO_TUTORIALS.filter(video => video.category === category);
}

export default HELP_TOPICS;
